import React, { useEffect, useState } from 'react';
import { Modal, Button, Row, Col, Space, Typography, Result, Divider, Spin } from 'antd';
import { DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { TranscriptExportFormat } from '../types';
import is from '../locales/is';

interface ExportTranscriptModalProps {
  isOpen: boolean;
  toggleModal: () => void;
  onExport: (format: TranscriptExportFormat, noDownload?: boolean) => void;
  waitingForExport: boolean;
  error?: string;
  dismissError: () => void;
}

interface ExportTranscriptModalState {
  currentHoverItem: string;
  hoverDisabled: boolean;
}

const { Paragraph } = Typography;

const ExportTranscriptModalError: React.FC<{ message: string }> = (props) => {
  const translateErrorMessage: (error: string) => string[] = (error: string) => {
    switch (error) {
      case 'smart_srt':
        return [
          is.transcript.export.downloadErrorMessageSmartSrtA,
          '',
          is.transcript.export.downloadErrorMessageSmartSrtB,
          '',
          is.transcript.export.downloadErrorMessageSmartSrtC,
          is.transcript.export.downloadErrorMessageSmartSrtD,
        ];
      default:
        return [is.transcript.export.downloadErrorMessageDefault];
    }
  };

  const errorLines = translateErrorMessage(props.message);

  return (
    <Result
      status='error'
      icon={<ExclamationCircleOutlined />}
      title={is.transcript.export.downloadError}
      subTitle={
        <div>
          {errorLines.map((line, idx) => {
            if (line === '') {
              return <br key={idx} />;
            }
            return <Paragraph key={idx}>{line}</Paragraph>;
          })}
          <Divider />
          <Paragraph>
            <span dangerouslySetInnerHTML={{ __html: is.transcript.export.downloadErrorContact }} />
          </Paragraph>
        </div>
      }
    />
  );
};

const ExportTranscriptModal: React.FC<ExportTranscriptModalProps> = (props) => {
  const [state, setState] = useState<ExportTranscriptModalState>({
    hoverDisabled: false,
    currentHoverItem: '',
  });

  const { currentHoverItem, hoverDisabled } = state;
  const { isOpen, toggleModal, onExport, waitingForExport, error, dismissError } = props;

  useEffect(() => {
    if (!error) return;
    setState({ ...state, currentHoverItem: '', hoverDisabled: true });
    setTimeout(() => {
      setState({ ...state, hoverDisabled: false });
    }, 5000);
  }, [error]);

  const handleMouseOver = (text: string) => {
    if (text === currentHoverItem || hoverDisabled) return;
    error && dismissError();
    setState({ ...state, currentHoverItem: text });
  };

  const handleExport = (format: TranscriptExportFormat) => {
    hoverDisabled && setState({ ...state, hoverDisabled: false });
    error && dismissError();
    onExport(format);
  };

  const onClose = () => {
    error && dismissError();
    toggleModal();
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      closable={true}
      width='40%'
      title={
        <>
          <DownloadOutlined style={{ marginRight: '8px' }} />
          {is.transcript.export.formatChoose}
        </>
      }
      footer={null}
    >
      <Row gutter={16}>
        <Col span={8}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-around',
              height: '100%',
            }}
          >
            <Space direction='vertical' style={{ width: '100%' }}>
              <Button
                block
                onMouseEnter={() => handleMouseOver(is.transcript.export.formatDescriptionTxt)}
                onClick={() => handleExport('txt')}
              >
                {is.transcript.export.formatTitleTxt}
              </Button>
              <Button
                block
                onMouseEnter={() => handleMouseOver(is.transcript.export.formatDescriptionSrt)}
                onClick={() => handleExport('srt')}
              >
                {is.transcript.export.formatTitleSrt}
              </Button>
              <Button
                block
                onMouseEnter={() => handleMouseOver(is.transcript.export.formatDescriptionSmartSrt)}
                onClick={() => handleExport('smart_srt')}
              >
                {is.transcript.export.formatTitleSmartSrt}
              </Button>
              <Button
                block
                onMouseEnter={() => handleMouseOver(is.transcript.export.formatDescriptionAudacity)}
                onClick={() => handleExport('audacity')}
              >
                {is.transcript.export.formatTitleAudacity}
              </Button>
              <Button
                block
                onMouseEnter={() => handleMouseOver(is.transcript.export.formatDescriptionWebVtt)}
                onClick={() => handleExport('vtt_paint_on')}
              >
                {is.transcript.export.formatTitleWebVtt}
              </Button>
            </Space>
          </div>
        </Col>
        <Col
          span={16}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '250px',
          }}
        >
          <Spin spinning={waitingForExport}>
            {error ? (
              <ExportTranscriptModalError message={error} />
            ) : (
              <Typography.Text>{currentHoverItem}</Typography.Text>
            )}
          </Spin>
        </Col>
      </Row>
    </Modal>
  );
};

export default ExportTranscriptModal;
